import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { combineLatest, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import {
  NOTIFICATIONS_POLL_INTERVAL_MS,
  pollTicksWhileDocumentVisible,
} from '../../utils/page-visibility-poll';
import { Store } from '@ngrx/store';
import type { AppState } from '../app.state';
import { NotificationsService, type NotificationType } from '../../services/notifications.service';
import * as NotificationsActions from './notifications.actions';
import {
  selectNotificationsFilter,
  selectNotificationsPage,
  selectNotificationsPageSize,
} from './notifications.selectors';
import type { NotificationsFilter } from './notifications.types';

@Injectable()
export class NotificationsEffects {
  private actions$ = inject(Actions);
  private store = inject(Store<AppState>);
  private notificationsService = inject(NotificationsService);

  private static readonly APPOINTMENT_TYPES: NotificationType[] = [
    'appointment_new',
    'appointment_modified',
    'appointment_cancelled_or_rejected',
  ];

  private filterToQuery(filter: NotificationsFilter): {
    unreadOnly?: boolean;
    types?: NotificationType[];
  } {
    switch (filter) {
      case 'unread':
        return { unreadOnly: true };
      case 'read':
        return { unreadOnly: false };
      case 'appointments':
        return { types: NotificationsEffects.APPOINTMENT_TYPES };
      case 'appointments_unread':
        return { unreadOnly: true, types: NotificationsEffects.APPOINTMENT_TYPES };
      case 'chat':
        return { types: ['conversation_intervention_needed'] };
      case 'chat_unread':
        return { unreadOnly: true, types: ['conversation_intervention_needed'] };
      case 'system':
        return { types: ['system_error'] };
      case 'new_member':
        return { types: ['tenant_new_member'] };
      case 'all':
      default:
        return {};
    }
  }

  loadNotificationsPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationsActions.loadNotificationsPage),
      switchMap(({ filter, page, pageSize }) => {
        const query = this.filterToQuery(filter);
        return this.notificationsService
          .getNotifications({
            ...query,
            page,
            pageSize,
          })
          .pipe(
            map((res) =>
              NotificationsActions.loadNotificationsPageSuccess({
                items: res.items,
                totalItems: res.total,
              })
            ),
            catchError((error) =>
              of(
                NotificationsActions.loadNotificationsPageFailure({
                  error: error?.message ?? 'Failed to load notifications',
                })
              )
            )
          );
      })
    )
  );

  markNotificationAsRead$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationsActions.markNotificationAsRead),
      switchMap(({ id }) =>
        this.notificationsService.markAsRead(id).pipe(
          map(() => NotificationsActions.markNotificationAsReadSuccess({ id })),
          catchError((error) =>
            of(
              NotificationsActions.markNotificationAsReadFailure({
                id,
                error: error?.message ?? 'Failed to mark as read',
              })
            )
          )
        )
      )
    )
  );

  // Reload current page (no loading spinner) after marking read
  reloadAfterMarkRead$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationsActions.markNotificationAsReadSuccess),
      switchMap(() =>
        combineLatest([
          this.store.select(selectNotificationsFilter),
          this.store.select(selectNotificationsPage),
          this.store.select(selectNotificationsPageSize),
        ]).pipe(
          take(1),
          switchMap(([filter, page, pageSize]) =>
            of(
              NotificationsActions.loadNotificationsPage({
                filter,
                page,
                pageSize,
                showLoading: false,
              })
            )
          )
        )
      )
    )
  );

  unreadCountPolling$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationsActions.startUnreadCountPolling),
      switchMap(() =>
        pollTicksWhileDocumentVisible(NOTIFICATIONS_POLL_INTERVAL_MS).pipe(
          switchMap(() =>
            this.notificationsService.getUnreadNotificationsCount().pipe(
              map((unreadCount) => NotificationsActions.setUnreadCount({ unreadCount })),
              catchError(() => of(NotificationsActions.setUnreadCount({ unreadCount: 0 })))
            )
          )
        )
      )
    )
  );
}
